import { getRenewalReturnPath } from '../utils/billingReturn';
import { useDialogFocus } from '../v2/useDialogFocus';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth, database } from '../firebase/firebaseConfig';
import { Helmet } from 'react-helmet-async';
import { get, ref } from 'firebase/database';
import '../v2/workflows.css';

const LOGIN_ERROR_STORAGE_KEY = "shopLoginError";

const saveLoginError = (message) => {
    if (!message) return;

    localStorage.setItem(
        LOGIN_ERROR_STORAGE_KEY,
        JSON.stringify({
            message,
            createdAt: Date.now(),
        })
    );
};

const ShopLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [isLoging, setIsLoging] = useState(false);
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const accountCreation = searchParams.get("success") === "true";
    const reviewEmailFailed = searchParams.get("reviewEmail") === "failed";
    const blockedStatus = searchParams.get("status");
    const subscriptionRequired = searchParams.get("subscription") === "required";
    const [showAccountCreatedPopup, setShowAccountCreatedPopup] = useState(false);


    const [showSubscriptionSuccessPopup, setShowSubscriptionSuccessPopup] = useState(false);
    const subscriptionActivated = searchParams.get("subscription") === "active";
    useDialogFocus(showAccountCreatedPopup || showSubscriptionSuccessPopup);

    useEffect(() => {
        // A completed payment return supersedes a cached pre-payment notice.
        // This only clears presentation state; sign-in still reads the account.
        if (subscriptionActivated) {
            localStorage.removeItem(LOGIN_ERROR_STORAGE_KEY);
            setError('');
            return;
        }
        const storedError = localStorage.getItem(LOGIN_ERROR_STORAGE_KEY);

        if (!storedError) {
            return;
        }

        try {
            const parsed = JSON.parse(storedError);

            if (parsed?.message) {
                setError(parsed.message);
            }
        } catch (storageError) {
            console.error(
                "Failed to restore login error:",
                storageError
            );
        } finally {
            // Show it only once after refresh.
            localStorage.removeItem(LOGIN_ERROR_STORAGE_KEY);
        }
    }, [subscriptionActivated]);

    useEffect(() => {
        if (accountCreation) {
            setShowAccountCreatedPopup(true);

            const params = new URLSearchParams(location.search);
            params.delete("success");

            navigate(
                `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`,
                { replace: true }
            );
        }
    }, [accountCreation, navigate, location]);

    useEffect(() => {
        if (!subscriptionActivated) {
            return;
        }

        setShowSubscriptionSuccessPopup(true);
        setError('');
        localStorage.removeItem(LOGIN_ERROR_STORAGE_KEY);

        // Remove the query parameter after detecting it.
        const params = new URLSearchParams(location.search);
        params.delete("subscription");

        sessionStorage.removeItem(
            "trialExpiredSubscription"
        );
        navigate(
            `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`,
            {
                replace: true,
            }
        );
    }, [
        subscriptionActivated,
        navigate,
        location.pathname,
        location.search,
    ]);

    useEffect(() => {
        // Older releases cached a trial-blocking dialog. Approved owners now keep
        // access to existing work, and billing is managed from account settings.
        sessionStorage.removeItem("trialExpiredSubscription");
    }, []);

    useEffect(() => {
        if (reviewEmailFailed) {
            setError("Your account is under review. We could not send the confirmation email, so please contact support.");
        }
        if (subscriptionRequired) setError("Your subscription needs attention. Sign in to review renewal options.");
        if (blockedStatus === "rejected") {
            setError("Your account application was rejected. Please contact support for more information.");
        } else if (blockedStatus === "review") {
            setError("Your account is under review. You will be able to sign in after it is approved.");
        }
    }, [reviewEmailFailed, blockedStatus, subscriptionRequired]);

    const showLoginError = (message) => {
        setError(message);
        saveLoginError(message);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoging(true);
        setError('');
        localStorage.removeItem(LOGIN_ERROR_STORAGE_KEY);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // 🔥 GET USER FROM REALTIME DATABASE
            const userRef = ref(database, 'Users/' + user.uid);
            const snapshot = await get(userRef);

            if (!snapshot.exists()) {
                showLoginError('No account data found!');
                return;
            }

            const userData = snapshot.val();

            // 🔐 check active status
            if (["printShop", "supplier", "specialist"].includes(userData.type) && userData.status === "payment_pending") {
                await signOut(auth);
                showLoginError("Your listing has been approved. Check your email for the secure first-payment link. Your listing goes live after payment is confirmed.");
                return;
            }
            if (["printShop", "supplier", "specialist"].includes(userData.type) && userData.status !== "approved") {
                await signOut(auth);
                showLoginError(userData.status === "rejected"
                    ? "Your account application was rejected. Please contact support for more information."
                    : "Your account is under review. You will be able to sign in after it is approved.");
                return;
            }

            // Billing expiry limits publication and new intake, not existing work.
            // The authenticated dashboard explains the current billing state.
            // 🔥 TYPE BASED REDIRECTION
            const type = userData?.type;

            // Redirect immediately; an orphaned timer can overwrite later workspace navigation.
                const billingReturn = getRenewalReturnPath(location.search);
                if (billingReturn && ['printShop','supplier','specialist'].includes(type)) {
                    navigate(billingReturn, { replace: true });
                }
                else if (type === 'printShop') {
                    navigate("/shop/dashboard", { replace: true });
                }
                else if (type === 'supplier' || type === 'specialist') {
                    navigate("/supplier/dashboard", { replace: true });
                }
                else if (type === 'admin') {
                    navigate("/admin/dashboard", { replace: true });
                }
                else {
                    setError("Invalid account type!");
                }


        } catch (err) {
            if (err.code === 'auth/user-not-found') {
                showLoginError('Email not found. Please check again.');
            } else if (err.code === 'auth/invalid-credential') {
                showLoginError('Incorrect password.');
            } else {
                console.log(err.code);
                showLoginError('Login failed. Please try again.');
            }
        } finally {
            setIsLoging(false);
        }
    };


    return (
        <div className="v2-auth-shell">
            <Helmet>
                <title>Store Login - Signet Print</title>
                <meta
                    name="description"
                    content="Login to your store account on Signet Print."
                />
            </Helmet>
            <main className="v2-auth-main">

                <div className="v2-auth-card">
                    <Link className="v2-flow-brand" to="/" aria-label="SignetPrint home"><span className="v2-flow-mark" aria-hidden="true"><svg viewBox="0 0 52 52" fill="currentColor"><path d="M26 52C40.36 52 52 40.36 52 26S40.36 0 26 0v52Z"/><path d="M0 52c14.36 0 26-11.64 26-26S14.36 0 0 0v52Z"/></svg></span><span>Signet<span>Print</span></span></Link>

                    {/* HEADER */}
                    <div className="v2-auth-title">

                        <h1>
                            Store login
                        </h1>

                        <p>
                            Sign in to see the files and questions your customers have sent you.
                        </p>
                    </div>

                    {/* FORM */}
                    <form
                        onSubmit={handleLogin}
                        className="v2-auth-form"
                    >

                        {/* ERROR */}
                        {
                            error && (
                                <div role="alert" className="bg-[#fff0f0] border border-[#ffcdd2] rounded-[10px] px-[16px] py-[12px]">
                                    <p className="font-medium text-[13px] text-[#d32f2f]">
                                        {error || "An error occurred while creating your account. Please try again."}
                                    </p>
                                </div>
                            )
                        }

                        {/* EMAIL */}
                        <div className="flex flex-col gap-[6px]">
                            <label className="font-semibold text-[14px] text-[#212326]">
                                Email
                            </label>

                            <div className="relative">
                                <svg className="absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9298a0]"
                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor" strokeWidth="2">
                                    <rect width="20" height="16" x="2" y="4" rx="2" />
                                    <path d="m22 7-10 5L2 7" />
                                </svg>

                                <input
                                    autoComplete="email"
                                    aria-label="Email"
                                    type="email"
                                    placeholder="shop@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full h-[48px] rounded-[12px] border border-[#e4e5e7] font-semibold pl-[40px] pr-[16px] text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none focus:border-[#ff5323]"
                                />
                            </div>
                        </div>

                        {/* PASSWORD */}
                        <div className="flex flex-col gap-[6px]">
                            <div className="flex items-center justify-between">
                                <label className="font-semibold text-[14px] text-[#212326]">
                                    Password
                                </label>

                                <Link
                                    to="/forgot-password"
                                    className="font-medium text-[13px] text-[#ff5323] hover:underline"
                                >
                                    Forgot password?
                                </Link>
                            </div>

                            <div className="relative">
                                <svg className="absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9298a0]"
                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor" strokeWidth="2">
                                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>

                                <input
                                    autoComplete="current-password"
                                    aria-label="Password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full h-[48px] rounded-[12px] border border-[#e4e5e7] font-semibold pl-[40px] pr-[44px] text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none focus:border-[#ff5323]"
                                />

                                <button
                                    type="button"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-[14px] top-1/2 -translate-y-1/2 text-[#9298a0] hover:text-[#212326]"
                                >
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off w-[18px] h-[18px]">
                                            <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"></path>
                                            <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path>
                                            <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path>
                                            <path d="m2 2 20 20"></path>
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye w-[18px] h-[18px]">
                                            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* SUBMIT */}
                        <button
                            type="submit"
                            disabled={isLoging}
                            className="flex items-center justify-center gap-[8px] w-full h-[48px] rounded-[12px] font-semibold text-[15px] bg-[#ff5323] text-white hover:bg-[#e64a1f] shadow-[0px_2px_8px_rgba(255,83,35,0.25)]"
                        >
                            {isLoging ? "Signing In..." : "Sign In"}

                            {!isLoging && (
                                <svg className="w-[16px] h-[16px]"
                                    xmlns="http://www.w3.org/2000/svg" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path d="M5 12h14" />
                                    <path d="m12 5 7 7-7 7" />
                                </svg>
                            )}
                        </button>

                        {/* BACK LINK STYLE */}
                        <p className="text-center font-medium text-[13px] text-[#9298a0]">
                            Don't have an account?{" "}
                            <Link to="/onboarding/plan" className="text-[#ff5323] font-semibold hover:underline">
                                Get started
                            </Link>
                        </p>

                    </form>
                </div>
            </main>

            {showSubscriptionSuccessPopup && (
                <div
                    className="
            fixed inset-0 z-[9999]
            flex items-center justify-center
            bg-black/50
            px-4
            backdrop-blur-sm
        "
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="subscription-success-title"
                >
                    <div
                        className="
                relative
                w-full max-w-[440px]
                overflow-hidden
                rounded-[20px]
                bg-white
                p-6 sm:p-8
                shadow-[0_20px_60px_rgba(0,0,0,0.20)]
            "
                    >
                        {/* Close */}
                        <button
                            type="button"
                            onClick={() => {
                                setShowSubscriptionSuccessPopup(false);
                            }}
                            className="
                    absolute right-4 top-4
                    flex h-9 w-9
                    items-center justify-center
                    rounded-full
                    text-[24px]
                    leading-none
                    text-[#9298a0]
                    transition
                    hover:bg-[#f5f5f6]
                    hover:text-[#212326]
                "
                            aria-label="Close"
                        >
                            ×
                        </button>

                        {/* Success Icon */}
                        <div className="flex justify-center">
                            <div
                                className="
                        flex h-16 w-16
                        items-center justify-center
                        rounded-full
                        bg-[#eaf8ef]
                    "
                            >
                                <svg
                                    className="h-8 w-8 text-[#22a55a]"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M5 12.5l4 4L19 7"
                                    />
                                </svg>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="mt-5 text-center">
                            <h2
                                id="subscription-success-title"
                                className="
                        text-[22px]
                        font-semibold
                        leading-[1.3]
                        text-[#212326]
                    "
                            >
                                Subscription Active
                            </h2>

                            <p
                                className="
                        mt-3
                        text-[14px]
                        font-medium
                        leading-[1.7]
                        text-[#717182]
                    "
                            >
                                Your payment was successful and your subscription
                                has been activated.
                            </p>

                            <p
                                className="
                        mt-2
                        text-[14px]
                        font-medium
                        leading-[1.7]
                        text-[#717182]
                    "
                            >
                                Please sign in again to continue to your account.
                            </p>
                        </div>

                        {/* Status */}
                        <div
                            className="
                    mt-5
                    flex items-center gap-3
                    rounded-[12px]
                    border border-[#d9f0e1]
                    bg-[#f3fbf6]
                    px-4 py-3
                "
                        >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#dff5e7]">
                                <svg
                                    className="h-4 w-4 text-[#22a55a]"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M5 12.5l4 4L19 7"
                                    />
                                </svg>
                            </div>

                            <div>
                                <p className="text-[13px] font-semibold text-[#227a43]">
                                    Payment successful
                                </p>

                                <p className="mt-0.5 text-[12px] font-medium text-[#5f806b]">
                                    Your account is ready to use.
                                </p>
                            </div>
                        </div>

                        {/* Continue */}
                        <button
                            type="button"
                            onClick={() => {
                                setShowSubscriptionSuccessPopup(false);
                            }}
                            className="
                    mt-6
                    flex h-[48px]
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-[12px]
                    bg-[#ff5323]
                    px-5
                    text-[15px]
                    font-semibold
                    text-white
                    shadow-[0_2px_8px_rgba(255,83,35,0.25)]
                    transition
                    hover:bg-[#e64a1f]
                "
                        >
                            Continue to Login

                            <svg
                                className="h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="M5 12h14" />
                                <path d="m12 5 7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}


            {showAccountCreatedPopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                    <div role="dialog" aria-modal="true" aria-label="Account submitted" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">

                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-100">
                            <svg
                                className="h-7 w-7 text-[#ff5323]"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 8v4m0 4h.01M22 12A10 10 0 1112 2a10 10 0 0110 10z"
                                />
                            </svg>
                        </div>

                        <h2 className="text-center text-xl font-semibold text-[#212326]">
                            Account Submitted
                        </h2>

                        <p className="mt-4 text-center text-[15px] leading-7 text-[#717182]">
                            Your account has been submitted successfully and is currently
                            <strong> under review.</strong>
                        </p>

                        <p className="mt-3 text-center text-[15px] leading-7 text-[#717182]">
                            Please check your <strong>Inbox</strong> and <strong>Spam</strong> folder for our approval email.
                            After approval, use the secure payment link in your email. Your listing and workspace activate after payment is confirmed.
                        </p>

                        <button
                            onClick={() => setShowAccountCreatedPopup(false)}
                            className="mt-6 h-12 w-full rounded-xl bg-[#ff5323] font-semibold text-white hover:bg-[#e64a1f]"
                        >
                            Got it
                        </button>

                    </div>
                </div>
            )}
        </div>
    )
}

export default ShopLogin;
